import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import { LogOut, ArrowLeft, Calendar, User, Building, MapPin, DollarSign, FileText, Download, Mail, Phone, Check, X, Archive } from 'lucide-react';
import CandidatureActionModal from './CandidatureActionModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

const ManagerOfferDetailPage = () => {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [offer, setOffer] = useState(null);
  const [candidatures, setCandidatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedCandidature, setSelectedCandidature] = useState(null);
  const [actionType, setActionType] = useState(null); // "accepte" or "refuse"
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationPassword, setValidationPassword] = useState('');
  const [validating, setValidating] = useState(false);

  useEffect(() => {
    fetchOfferDetails();
    fetchCandidatures();
  }, [offerId]);

  const fetchOfferDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/job-offers/${offerId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const offerData = await response.json();
        setOffer(offerData);
      } else {
        toast.error('Erreur lors du chargement de l\'offre');
        navigate('/manager/offres-en-cours');
      }
    } catch (error) {
      console.error('Error fetching offer details:', error);
      toast.error('Erreur de connexion');
      navigate('/manager/offres-en-cours');
    } finally {
      setLoading(false);
    }
  };

  const fetchCandidatures = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/candidatures/job/${offerId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const candidaturesData = await response.json();
        setCandidatures(candidaturesData);
      }
    } catch (error) {
      console.error('Error fetching candidatures:', error);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Déconnexion réussie');
  };

  const handleBackToOffres = () => {
    navigate('/manager/offres-en-cours');
  };

  const handleCandidatureAction = (candidature, action) => {
    setSelectedCandidature(candidature);
    setActionType(action);
    setShowActionModal(true);
  };

  const handleCloseActionModal = () => {
    setShowActionModal(false);
    setSelectedCandidature(null);
    setActionType(null);
  };

  const handleActionSuccess = () => {
    setShowActionModal(false);
    setSelectedCandidature(null);
    setActionType(null);
    fetchCandidatures(); // Refresh candidatures list
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non spécifiée';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'accepte':
        return 'candidature-status-compact status-accepte';
      case 'refuse':
        return 'candidature-status-compact status-refuse';
      default:
        return 'candidature-status-compact status-nouveau';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'accepte':
        return 'Accepté';
      case 'refuse':
        return 'Refusé';
      default:
        return 'Nouveau';
    }
  };

  const handleArchiveOffer = async () => {
    setValidating(false); // Reset validating state when opening modal
    setShowValidationModal(true);
  };

  const handleConfirmValidation = async () => {
    if (!validationPassword.trim()) {
      toast.error('Veuillez saisir votre mot de passe');
      return;
    }

    if (validating) {
      return; // Prevent multiple clicks
    }

    setValidating(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/job-offers/${offerId}/archive`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          password: validationPassword
        }),
      });

      if (response.ok) {
        toast.success('Offre archivée avec succès');
        setShowValidationModal(false);
        setValidationPassword('');
        // Navigate back to appropriate page based on user role
        if (user?.role === 'rh') {
          navigate('/rh/recrutements-generaux');
        } else {
          navigate('/manager/offres-en-cours');
        }
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Erreur lors de l\'archivage');
      }
    } catch (error) {
      console.error('Error archiving offer:', error);
      toast.error('Erreur de connexion');
    } finally {
      setValidating(false);
    }
  };

  const companyLogos = {
    'Ma Conciergerie': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/mruq8p60_PNG_MA%20CONCIERGERIE.png',
    'Mon Evenementiel': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/o94kh96a_logo-monevenemenciel_png.png',
    'SISENS': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/gwqu631h_25.04.10%20SISENS%20LOGO.png',
    'Prysme': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/nzxlkuw3_logo-prysme-couleur.png'
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!offer) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl text-destructive mb-4">Offre non trouvée</h1>
          <Button onClick={handleBackToOffres}>Retour aux offres</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="manager-offer-detail-page">
      {/* Header */}
      <header className="rh-header">
        <div className="flex items-center space-x-4">
          <Button
            onClick={handleBackToOffres}
            variant="outline"
            size="sm"
            className="btn-outline"
            style={{ borderColor: '#eb5d2d', color: '#eb5d2d' }}
          >
            <ArrowLeft size={16} className="mr-2" />
            Retour
          </Button>
          <img
            src={companyLogos[offer.company]}
            alt={`Logo ${offer.company}`}
            className="rh-header-logo"
          />
        </div>
        <h1>Détail de l'offre - {user?.company}</h1>
        <Button
          onClick={handleLogout}
          variant="outline"
          size="sm"
          className="btn-outline"
          style={{ borderColor: '#eb5d2d', color: '#eb5d2d' }}
        >
          <LogOut size={16} className="mr-2" />
          Déconnexion
        </Button>
      </header>

      {/* Main Content */}
      <main className="rh-main">
        {/* Offer Header */}
        <div className="offer-detail-header-compact">
          <h2>{offer.company} : {offer.title}</h2>
          <div className="offer-detail-info-line">
            <div className="offer-detail-info-item-compact">
              <User className="offer-detail-info-icon-compact" />
              <span>{offer.creator_name || 'Inconnu'}</span>
            </div>
            <div className="offer-detail-info-separator">•</div>
            <div className="offer-detail-info-item-compact">
              <Calendar className="offer-detail-info-icon-compact" />
              <span>{formatDate(offer.date_arrivee)}</span>
            </div>
            <div className="offer-detail-info-separator">•</div>
            <div className="offer-detail-info-item-compact">
              <MapPin className="offer-detail-info-icon-compact" />
              <span>{offer.lieu_travail || 'Non spécifié'}</span>
            </div>
            {offer.type_poste && (
              <>
                <div className="offer-detail-info-separator">•</div>
                <div className="offer-detail-info-item-compact">
                  <span>{offer.type_poste}</span>
                </div>
              </>
            )}
            {offer.taux_horaire && (
              <>
                <div className="offer-detail-info-separator">•</div>
                <div className="offer-detail-info-item-compact">
                  <DollarSign className="offer-detail-info-icon-compact" />
                  <span>{offer.taux_horaire}€/h</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Offer Description */}
        <Card className="offer-description-card-compact">
          <CardContent className="p-4">
            <h3 className="text-lg font-semibold mb-3">Description détaillée</h3>
            <div className="offer-description-content-compact">
              <div className="mb-3">
                <h4 className="font-medium mb-1 text-sm text-primary">Missions</h4>
                <p className="text-gray-700 text-sm whitespace-pre-wrap">{offer.description}</p>
              </div>
              {offer.qualites_attendues && (
                <div>
                  <h4 className="font-medium mb-1 text-sm text-primary">Qualités attendues</h4>
                  <p className="text-gray-700 text-sm whitespace-pre-wrap">{offer.qualites_attendues}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Candidatures Table */}
        <Card className="candidatures-card-compact">
          <CardContent className="p-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-semibold">
                Candidatures ({candidatures.length})
              </h3>
              {user?.role === 'rh' && offer?.status === 'en_cours' && (
                <Button
                  onClick={handleArchiveOffer}
                  variant="outline"
                  className="text-sm px-3 py-2"
                  style={{ borderColor: '#dc2626', color: '#dc2626' }}
                >
                  <Archive size={16} className="mr-2" />
                  Archiver
                </Button>
              )}
            </div>
            <div className="candidatures-table-container-compact">
              <table className="candidatures-table-compact">
                <thead>
                  <tr>
                    <th>Candidat</th>
                    <th>Documents</th>
                    <th>Contact</th>
                    <th>Commentaire</th>
                    <th>Statut</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {candidatures.map((candidature) => (
                    <tr key={candidature.id} className={candidature.status === 'refuse' ? 'candidature-refused' : ''}>
                      <td>
                        <div className="candidature-name-compact">
                          {candidature.prenom} {candidature.nom}
                        </div>
                      </td>
                      <td>
                        <div className="candidature-files-compact">
                          {candidature.cv_filename && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="mr-1 text-xs px-2 py-1"
                              onClick={() => window.open(`${API}/download/${candidature.cv_filename}`, '_blank')}
                            >
                              <Download size={12} className="mr-1" />
                              CV
                            </Button>
                          )}
                          {candidature.lettre_filename && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs px-2 py-1"
                              onClick={() => window.open(`${API}/download/${candidature.lettre_filename}`, '_blank')}
                            >
                              <FileText size={12} className="mr-1" />
                              LM
                            </Button>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="candidature-contact-compact">
                          {(candidature.manager_email || candidature.email) && (
                            <div className="contact-item-compact">
                              <Mail size={12} />
                              <span>{candidature.manager_email || candidature.email}</span>
                            </div>
                          )}
                          {(candidature.manager_telephone || candidature.telephone) && (
                            <div className="contact-item-compact">
                              <Phone size={12} />
                              <span>{candidature.manager_telephone || candidature.telephone}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="candidature-comment-compact">
                          {candidature.commentaire || '-'}
                          {candidature.raison_refus && (
                            <div className="refus-reason">
                              <strong>Refus:</strong> {candidature.raison_refus}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className={getStatusStyle(candidature.status)}>
                          {getStatusText(candidature.status)}
                        </div>
                      </td>
                      <td>
                        <div className="candidature-actions">
                          {candidature.status === 'nouveau' && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                className="mr-1 text-xs px-2 py-1 candidature-action-accept"
                                onClick={() => handleCandidatureAction(candidature, 'accepte')}
                              >
                                <Check size={12} className="mr-1" />
                                Accepté
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-xs px-2 py-1 candidature-action-refuse"
                                onClick={() => handleCandidatureAction(candidature, 'refuse')}
                              >
                                <X size={12} className="mr-1" />
                                Refusé
                              </Button>
                            </>
                          )}
                          {candidature.status !== 'nouveau' && (
                            <span className="text-gray-400 text-xs">Traité</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {candidatures.length === 0 && (
              <div className="empty-state-compact">
                <p>Aucune candidature pour cette offre</p>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Candidature Action Modal */}
      {showActionModal && selectedCandidature && (
        <CandidatureActionModal
          isOpen={showActionModal}
          onClose={handleCloseActionModal}
          onSuccess={handleActionSuccess}
          candidature={selectedCandidature}
          actionType={actionType}
        />
      )}

      {/* Validation Modal */}
      {showValidationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold mb-4">Archiver l'offre</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowValidationModal(false);
                  setValidationPassword('');
                  setValidating(false);
                }}
                className="p-1"
              >
                <X size={20} />
              </Button>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-3">
                Pour archiver cette offre, veuillez saisir votre mot de passe RH :
              </p>
              <input
                type="password"
                value={validationPassword}
                onChange={(e) => setValidationPassword(e.target.value)}
                placeholder="Mot de passe"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                disabled={validating}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !validating) {
                    handleConfirmValidation();
                  }
                }}
              />
            </div>
            
            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowValidationModal(false);
                  setValidationPassword('');
                  setValidating(false);
                }}
                disabled={validating}
              >
                Annuler
              </Button>
              <Button
                onClick={handleConfirmValidation}
                disabled={validating || !validationPassword.trim()}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {validating ? 'Archivage en cours...' : 'Archiver'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagerOfferDetailPage;